import React from "react"
import { Dialog, FlatButton, FontIcon, CircularProgress } from "material-ui"
import ImageLoader from "../misc/ImageLoader"
import AuditTable from "../audit/AuditTable"
import EditPunchForm from "./EditPunchForm"
import strings from "../../localization/strings"
import autobind from "class-autobind"
import * as punchActions from "../../actions/PunchActions"
import { IRecord, EPunchAuditAction, IPunchSetDocument, ITimecardEmployee, IUIUser, EExceptionType } from "../../models"
import * as api from "../../api"
import NearLocationsPunchMap from "./NearLocationsPunchMap"
import moment from "moment"
import * as timeHelper from "../../helpers/timeHelper"
import PunchQuestionAnswerChip from "./punch_question_answer_detail/PunchQuestionAnswerChip"
import { isMobile } from "react-device-detect"
import DeleteIcon from "material-ui/svg-icons/action/delete"
import { red500, grey900, grey100 } from "material-ui/styles/colors"
import ClientLocalDateTimeStr from "../common/ClientLocalDateTimeStr"
import HierarchyJobTimezoneStr from "../common/HierarchyJobTimezoneStr"
import * as punchHelper from "../../helpers/punchHelper"

interface IProps {
    readonly: boolean
    punchId: string
    punchSet: IPunchSetDocument
    callingByEquipment?: boolean
    onRequestClose(): void
    reloadTimecard(msg: string): void
    onRemovePunch(): void
    //we need to select hierarchy based on employee punch permission, not logged in user punch permission (when the user has more punch rights than the admin)
    //it's possible when full timecard, for example, employee->timecard and payroll->timecard
    //timecards page is not full timecard in the case filter by punch level
    isFullTimecard?: boolean
    employee?: ITimecardEmployee

    //PriorPeriodAdjustment case
    allowPriorPeriodAdjust?: boolean
    originalTimecard?: boolean
}
interface IState {
    loading: boolean
    punchRecord: IRecord
    employee: ITimecardEmployee
    saving: boolean
}

export default class PunchDetailsDialog extends React.Component<IProps, IState> {
    private EditPunchFormForEmployee
    constructor(props: IProps) {
        super(props)
        this.state = {
            loading: true,
            punchRecord: null,
            employee: props.employee ? props.employee : null,
            saving: false
        }
        autobind(this)
    }

    async componentDidMount() {
        try {
            let punchRecord: IRecord
            if (this.props.originalTimecard) {
                punchRecord = punchHelper.getPunchRecordFromPunchSet(this.props.punchId, this.props.punchSet)
            }
            const res = await api.getPunchDetail(this.props.punchId, true)
            if (this.props.originalTimecard) {
                punchRecord.Punch.MetaData = res.data.Punch.MetaData
                punchRecord.Punch.Btn1Ids = res.data.Punch.Btn1Ids
                punchRecord.Punch.Btn2Ids = res.data.Punch.Btn2Ids
                punchRecord.Punch.ImageID = res.data.Punch.ImageID
                punchRecord.Punch.ImageS3Url = res.data.Punch.ImageS3Url
            }
            else {
                punchRecord = res.data as IRecord
            }
            this.state.employee || !punchRecord.Punch.UserId ?
                this.setState({
                    punchRecord,
                    loading: false
                }) :
                api.getUser(punchRecord.Punch.UserId).then(res1 => {
                    this.setState({
                        punchRecord,
                        employee: res1.data,
                        loading: false
                    })
                })
        }
        catch (err) {
            this.setState({ loading: false })
        }
    }

    async handleRequestSave() {
        const { hierarchyLevelId, errorHierarchyId, positionId, errorPunchPosition, payClassId, equipment, payTypeId, errorPayTypeId, job, phase, punchPosition, dateTime, dayGroupingDate } = this.EditPunchFormForEmployee.state
        if (errorHierarchyId) return
        if (this.state.employee && errorPayTypeId) return
        if (errorPunchPosition) return

        const equipmentId = equipment ? equipment.Id : null
        const jobId = job ? job.Id : null
        const phaseId = phase ? phase.Id : null
        const dateTimeMoment = moment(dateTime)
        const dayGroupingDateMoment = moment(dayGroupingDate)

        const punch = this.state.punchRecord.Punch
        const punchDateTimeMoment = moment(timeHelper.keepConvertUTCToLocal(punch.RoundingRuleId ? punch.RoundedDateTimeLocal : punch.DateTimeLocal))
        const punchDayGroupingDateMoment = moment(timeHelper.keepConvertUTCToLocal(punch.DayGroupingTimeLocal))

        if (
            punch.HierarchyItemId !== hierarchyLevelId ||
            punch.PositionId !== positionId ||
            punch.PayClassId !== payClassId ||
            punch.EquipmentId !== equipmentId ||
            punch.PayTypeId !== payTypeId ||
            punch.JobId !== jobId ||
            punch.PhaseId !== phaseId ||
            punchDateTimeMoment.year() !== dateTimeMoment.year() ||
            punchDateTimeMoment.month() !== dateTimeMoment.month() ||
            punchDateTimeMoment.date() !== dateTimeMoment.date() ||
            punchDateTimeMoment.hour() !== dateTimeMoment.hour() ||
            punchDateTimeMoment.minute() !== dateTimeMoment.minute() ||
            punchDayGroupingDateMoment.year() !== dayGroupingDateMoment.year() ||
            punchDayGroupingDateMoment.month() !== dayGroupingDateMoment.month() ||
            punchDayGroupingDateMoment.date() !== dayGroupingDateMoment.date()
        ) {
            const HierarchyItemId = hierarchyLevelId ? hierarchyLevelId : null
            const updatePunch = {
                HierarchyItemId,
                PositionId: positionId,
                PayClassId: payClassId,
                EquipmentId: equipment ? equipment.Id : null,
                PayTypeId: payTypeId,
                JobId: job && job.Id,
                JobName: job && job.Name,
                JobCostCode: job && job.CostCode,
                PhaseId: job && phase && phase.Id,
                PhaseName: job && phase && phase.Name,
                PhaseCostCode: job && phase && phase.CostCode,
                DayGroupingTimeLocal: moment(dayGroupingDate).utc(true).format(),
                TicksSinceChange: punch.TicksSinceChange
            }
            if (
                punchDateTimeMoment.year() !== dateTimeMoment.year() ||
                punchDateTimeMoment.month() !== dateTimeMoment.month() ||
                punchDateTimeMoment.date() !== dateTimeMoment.date() ||
                punchDateTimeMoment.hour() !== dateTimeMoment.hour() ||
                punchDateTimeMoment.minute() !== dateTimeMoment.minute()
            ) {
                const timezoneId = await timeHelper.getIANATimezoneNameFromHierarchyJobId(HierarchyItemId, jobId)
                updatePunch["DateTime"] = timeHelper.getTimezoneTranslatedDateTime(dateTime, dateTimeMoment.minute(), dateTimeMoment.hour(), timezoneId)
            }
            this.setState({ saving: true }, () =>
                punchActions.patchPunch(this.state.employee ? this.state.employee.Id : null, punch.Id, updatePunch, true, punchPosition, this.props.allowPriorPeriodAdjust,
                    (msg) => {
                        this.props.reloadTimecard(msg)
                        this.props.onRequestClose()
                    }
                )
            )
        }
        else {
            this.props.onRequestClose()
        }
    }

    render() {
        let timeString
        if (!this.state.loading && this.state.punchRecord) {
            timeString = moment(timeHelper.keepConvertUTCToLocal(this.state.punchRecord.Punch.DateTimeLocal)).format("h:mm:ssa MM/DD/YYYY")
            const roundedString = moment(timeHelper.keepConvertUTCToLocal(this.state.punchRecord.Punch.RoundedDateTimeLocal)).format("h:mm:ssa MM/DD/YYYY")
            if (this.state.punchRecord.Punch.RoundingRuleId) {
                timeString = roundedString + " " + strings.RoundedFrom + " " + timeString
            }
        }
        let timeFraud = !this.state.loading &&
            this.state.punchRecord.Punch.PunchExceptions &&
            this.state.punchRecord.Punch.PunchExceptions.find(i => i.TypeEnum === EExceptionType.TIME_FRAUD)
        return (
            <Dialog
                open
                autoScrollBodyContent
                title={timeFraud ? strings.Time_Fraud_Alert : strings.Punch_Details}
                titleStyle={{ color: timeFraud && red500 }}
                actions={!this.state.loading && [
                    isMobile && !this.state.punchRecord.Punch.Calculated &&
                    <FlatButton
                        secondary
                        label={strings.Delete_Punch}
                        style={{ float: "left" }}
                        labelStyle={{ paddingLeft: 0 }}
                        icon={<DeleteIcon style={{ marginTop: -2 }} />}
                        onClick={this.props.onRemovePunch}
                    />,
                    <FlatButton
                        data-intercom-target={"Btn-Close-PunchDetailDialog"}
                        key="cancel"
                        label={this.state.punchRecord.Punch.Calculated ? strings.Close : strings.CANCEL}
                        primary={this.state.punchRecord.Punch.Calculated}
                        secondary={!this.state.punchRecord.Punch.Calculated}
                        onClick={this.props.onRequestClose}
                    />,
                    !this.state.punchRecord.Punch.Calculated &&
                    <FlatButton
                        data-intercom-target={"Btn-Save-PunchDetail"}
                        key="ok"
                        label={strings.OK}
                        primary
                        disabled={this.state.saving}
                        onClick={this.state.punchRecord ? this.handleRequestSave : this.props.onRequestClose}
                    />
                ]}
            >
                {this.state.loading ?
                    <div style={{ textAlign: "center" }}>
                        <CircularProgress />
                    </div> :
                    this.state.punchRecord ?
                        <div>
                            <div style={{ width: 500, fontSize: 13 }}>
                                <span style={{ fontWeight: "bold" }}>{strings.Time_Of_Punch}:</span>&nbsp;
                                {timeString}&nbsp;
                                <HierarchyJobTimezoneStr
                                    hierarchyId={this.state.punchRecord.Punch.HierarchyItemId}
                                    jobId={this.state.punchRecord.Punch.JobId}
                                    localTime={timeHelper.keepConvertUTCToLocal(this.state.punchRecord.Punch.RoundedDateTimeLocal)}
                                />
                                <br></br>
                                <span style={{ fontWeight: "bold" }}>{strings.Time_Of_Punch_Upload}:</span>&nbsp;
                                {this.state.punchRecord &&
                                    <ClientLocalDateTimeStr
                                        hierarchyId={this.state.punchRecord.Punch.HierarchyItemId}
                                        jobId={this.state.punchRecord.Punch.JobId}
                                        utcTime={this.state.punchRecord.Punch.MetaData.UploadDateTime}
                                        formatStr="h:mm:ssa MM/DD/YYYY"
                                    />
                                }
                                &nbsp;
                                <HierarchyJobTimezoneStr
                                    hierarchyId={this.state.punchRecord.Punch.HierarchyItemId}
                                    jobId={this.state.punchRecord.Punch.JobId}
                                />
                            </div>
                            {(this.state.punchRecord.Punch.ImageS3Url ||
                                this.state.punchRecord.Punch.DeferredPhotoUpload ||
                                (this.state.punchRecord.Punch.MetaData.Latitude !== 0 && this.state.punchRecord.Punch.MetaData.Longitude !== 0)
                            ) &&
                                <div style={{ height: 150, display: "flex" }}>
                                    {this.state.punchRecord.Punch.MetaData.Latitude !== 0 && this.state.punchRecord.Punch.MetaData.Longitude !== 0 &&
                                        <div style={{ height: "100%", width: "100%" }}>
                                            <NearLocationsPunchMap
                                                record={this.state.punchRecord}
                                            />
                                        </div>
                                    }
                                    {this.state.punchRecord.Punch.ImageS3Url ?
                                        <div>
                                            <ImageLoader url={this.state.punchRecord.Punch.ImageS3Url} fixedHeight={150} key={this.state.punchRecord.Punch.Id} src={"/punches/image/" + this.state.punchRecord.Punch.ImageID} />
                                        </div> :
                                        this.state.punchRecord.Punch.DeferredPhotoUpload &&
                                        <div style={{ textAlign: "center", width: 195, height: 128, padding: 10, backgroundColor: "#efefef", border: "solid 1px #cccccc" }}>
                                            <b>{strings.DefferedImageUploadTitle}</b>
                                            <FontIcon className="fa fa-cloud-upload" style={{ marginTop: 10, fontSize: 60, color: "#cccccc" }} />
                                            <div>{strings.DefferedImageUploadBody}</div>
                                        </div>
                                    }
                                </div>
                            }
                            {this.state.punchRecord.Punch.Note &&
                                <div style={{ margin: 10, backgroundColor: grey100 }}>
                                    <span style={{ fontSize: 15, fontWeight: "bold", color: grey900, marginRight: 10 }}>{`${strings.Punch_Comment}:`}</span>
                                    <span style={{ fontSize: 14, fontWeight: "bold", color: grey900 }} title={this.state.punchRecord.Punch.Note}>{this.state.punchRecord.Punch.Note}</span>
                                </div>
                            }
                            {this.props.punchSet.PositionTasks && this.props.punchSet.PositionTasks.length > 0 &&
                                <div style={{ fontSize: 10, paddingTop: 10 }}>
                                    {this.props.punchSet.PositionTasks.map((task, i) =>
                                        <div key={i}>
                                            <i className="fa fa-check" /> {task.Name.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                            }
                            {this.props.punchSet.SignatureImageS3Url &&
                                <img src={this.props.punchSet.SignatureImageS3Url} style={{ width: 100 }} />
                            }
                            <div style={{ clear: "both" }} />
                            <div style={{ marginTop: 10 }}>
                                <EditPunchForm
                                    callingByEquipment={this.props.callingByEquipment}
                                    ref={item => this.EditPunchFormForEmployee = item}
                                    punch={this.state.punchRecord.Punch}
                                    punchSet={this.props.punchSet}
                                    user={this.state.employee as IUIUser}
                                    readonly={this.props.readonly || this.state.punchRecord.Punch.PunchAudits.find(i => i.PunchAuditAction === EPunchAuditAction.AutoSplitOnDayBoundry) !== undefined}
                                    isFullTimecard={this.props.isFullTimecard}
                                    handleForceUpdateDialog={() => this.forceUpdate()}
                                />
                            </div>
                            <div style={{ padding: 10, borderTop: "solid 1px #cccccc", position: "relative" }}>
                                <div style={{ position: "absolute", right: 10, top: -40 }}>
                                    <PunchQuestionAnswerChip
                                        imageS3Url={this.state.punchRecord && this.state.punchRecord.Punch.ImageS3Url ?
                                            this.state.punchRecord.Punch.ImageS3Url :
                                            this.state.employee &&
                                            this.state.employee.ProfileImageS3Url
                                        }
                                        punchDateTimeLocal={this.state.punchRecord ? this.state.punchRecord.Punch.DateTimeLocal : null}
                                        btn1Ids={this.state.punchRecord.Punch.Btn1Ids}
                                        btn2Ids={this.state.punchRecord.Punch.Btn2Ids}
                                    />
                                </div>
                                {!this.props.originalTimecard &&
                                    <AuditTable
                                        audits={this.state.punchRecord.Punch.PunchAudits}
                                        hierarchyId={this.state.punchRecord.Punch.HierarchyItemId}
                                        jobId={this.state.punchRecord.Punch.JobId}
                                    />
                                }
                            </div>
                        </div> :
                        <div>
                            {strings.This_punch_is_not_valid}
                        </div>
                }
            </Dialog >
        )
    }
}